import React, { useMemo, useCallback } from 'react';

import { useIntl, FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../hooks/connectAuth';

import Help from '../../../../presentational/Auth/Help';
import ProviderAuthGroup from '../../../Auth/ProviderGroup/ProviderAuthGroup';
import AuthProvider from '../../../Auth/ProviderGroup/Store/AuthContext';
import { FadeIn } from '../../../Common/Animations/Keyframes';
import Button from '../../../Common/Form/Button';
import { Steps } from '../../Constants';
import { Types as ContainerTypes, multipleActions } from '../../Store/ContainerReducer';
import { Types } from '../../Store/LoginReducer';
import { IAuthState } from '../../Store/Reducers';
import { Types as SignUpTypes } from '../../Store/SignUpReducer';
import AuthAppButtons from './AuthAppButtons';
import BirthData from './BirthData';
import PersonalData from './PersonalData';
import SignUpButton from './SignUpButton';

interface ISignUp {
    step: string,
    theme: string,
    showAppButtons: boolean
    allowBottomTermsWarning?: boolean
}

type TSignUp = IConnectedProps<ISignUp>;

const ButtonsContainer = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: ${({ theme }) => theme.space.bee};

    > ${Button} {
        width: 100%;
    }

    > .or-separator {
        color: ${({ theme }) => theme.colors.white};
    }
`;

const AnchorContainer = styled.span`
    display: initial;
    text-align: center;
    color: ${({ theme }) => theme.name === 'dark' ? theme.colors.white : theme.colors.gray900};

    > a {
        padding: 0;
        width: auto;
        height: auto;
        display: initial;
        margin-left: 3px;
        text-decoration: underline;
    }
`;

const Content = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: center;
    animation: ${FadeIn} .3s ease-out;
    gap:  ${({ theme }) => theme.space.cat};

    & > p {
        width: 100%;
        font-size: 0.75em;
        text-align: center;
        line-height: normal;
        padding: 0 2.0938rem;
        margin: 0.8125em 0 0.9375rem;
        color: ${({ theme }) => theme.colors.blue200};
    }

    @media only screen and (min-width: ${({ theme }) => theme.breakpoints.tablet.min}) {
        & > u {
            font-size: 1rem;
        }

        & > p {
            font-size: 0.9375rem;
            padding: 0 1.0938rem;
        }
    }
`;

const SignUp: React.FC<TSignUp> = ({ step, theme, showAppButtons, allowBottomTermsWarning, dispatch }) => {
    const intl = useIntl();
    const handleClickForgotPassword = (showFacebookMessage?: boolean) => dispatch({
        type: Types.FORGOT_PASSWORD,
        data: {
            open: true,
            showFacebookMessage
        }
    });

    const handleClickSignUp = useCallback(() => multipleActions(dispatch, [
        {
            type: ContainerTypes.CHANGE_STEP,
            data: Steps.LOGIN
        },
        {
            type: SignUpTypes.CHANGE_PERSONAL_FIELD_DATA,
            field: 'email',
            data: { error: '' }
        },
        {
            type: SignUpTypes.CHANGE_PERSONAL_FIELD_DATA,
            field: 'password',
            data: { error: '' }
        }
    ]), [dispatch]);

    const isSynastryInvite = theme === 'SynastryInvite';

    const handleClickAppInvite = useCallback(() => dispatch({
        type: ContainerTypes.CHANGE_STEP,
        data: Steps.LOGIN
    }), [dispatch]);

    const appInviteLink = useMemo(() => {
        if (step !== Steps.BIRTH_DATA || !showAppButtons) return null;

        return (
            <FormattedMessage id="authFunctional.appInvite.text">
                {(text: string) => (
                    <AnchorContainer>
                        {text}

                        <Button
                            variation="text"
                            onClick={handleClickAppInvite}
                        >
                            <FormattedMessage id="authFunctional.appInvite.button" />
                        </Button>
                    </AnchorContainer>
                )}
            </FormattedMessage>
        );
    }, [step, showAppButtons, handleClickAppInvite]);

    const haveAccountLink = useMemo(() => {
        if (step !== Steps.BIRTH_DATA || isSynastryInvite) return null;

        return (
            <FormattedMessage id="authFunctional.haveAccount.text">
                {(text: string) => (
                    <AnchorContainer>
                        {text}

                        <Button
                            variation="text"
                            onClick={handleClickSignUp}
                        >
                            <FormattedMessage id="authFunctional.haveAccount.button" />
                        </Button>
                    </AnchorContainer>
                )}
            </FormattedMessage>
        );
    }, [step, showAppButtons, isSynastryInvite, handleClickSignUp]);

    const renderThirdPartyAuthProviders = useCallback(() => {
        if (step !== Steps.BIRTH_DATA) return null;

        return (
            <>
                <FormattedMessage id="authFunctional.birthData.or">
                    {(text: string) => (<span className='or-separator'>{text}</span>)}
                </FormattedMessage>

                <AuthProvider
                    onClickFacebookButton={() => handleClickForgotPassword(true)}
                    actionText={{
                        apple: intl.formatMessage({ id: 'authFunctional.thirdPartyAuthProviders.signup.apple' }),
                        google: intl.formatMessage({ id: 'authFunctional.thirdPartyAuthProviders.signup.google' }),
                        facebook: intl.formatMessage({ id: 'authFunctional.thirdPartyAuthProviders.signup.facebook' })
                    }}
                >
                    <ProviderAuthGroup />
                </AuthProvider>
            </>
        );
    }, [step]);

    return (
        <Content data-cy="auth-sign-up-container">
            {step === Steps.PERSONAL_DATA
                ? <PersonalData />
                : <BirthData />
            }

            <ButtonsContainer>
                <SignUpButton />

                {appInviteLink}

                {renderThirdPartyAuthProviders()}

                {allowBottomTermsWarning && <Help classNames="signup__terms-and-policy-informative" />}

                {haveAccountLink}
            </ButtonsContainer>

            <AuthAppButtons />
        </Content>
    );
};

const mapStateToProps: IAuthMapStateToProps<ISignUp> = (state, isComparison = false) => {
    if (isComparison) {
        return {
            step: state.step,
            theme: state.theme,
            showAppButtons: state.showAppButtons,
            allowBottomTermsWarning: state.allowBottomTermsWarning
        };
    }

    return {
        step: (state as IAuthState).Container.step,
        theme: (state as IAuthState).Container.theme,
        showAppButtons: (state as IAuthState).Container.showAppButtons,
        allowBottomTermsWarning: (state as IAuthState).Container.allowBottomTermsWarning
    };
};

export default connectAuth(mapStateToProps)(SignUp);
